import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  roleFormSchema,
  type CreateRolePayload,
  type Role,
  type RoleFormValues,
  type UpdateRolePayload,
} from '../types/role.types';
import { useCreateRole } from './useCreateRole';
import { useUpdateRole } from './useUpdateRole';

interface UseRoleFormParams {
  role: Role | null;
  open: boolean;
  onClose: () => void;
}

const EMPTY_DEFAULTS: RoleFormValues = {
  name: '',
  description: '',
  isActive: true,
};

function toFormValues(role: Role | null): RoleFormValues {
  if (!role) return EMPTY_DEFAULTS;
  return {
    name: role.name,
    description: role.description ?? '',
    ...(role.userTypeId !== null ? { userTypeId: role.userTypeId } : {}),
    isActive: role.isActive,
  };
}

function buildCreatePayload(values: RoleFormValues): CreateRolePayload {
  const description = values.description.trim();
  const payload: CreateRolePayload = {
    name: values.name,
    isActive: values.isActive,
  };
  if (description.length > 0) payload.description = description;
  if (values.userTypeId !== undefined) payload.userTypeId = values.userTypeId;
  return payload;
}

export function useRoleForm({ role, open, onClose }: UseRoleFormParams) {
  const isEditMode = role !== null;
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: toFormValues(role),
    mode: 'onChange',
  });

  useEffect(() => {
    form.reset(open ? toFormValues(role) : EMPTY_DEFAULTS);
  }, [role, open, form]);

  const createMutation = useCreateRole(onClose);
  const updateMutation = useUpdateRole(onClose);

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending;

  function submit(values: RoleFormValues) {
    if (isEditMode && role) {
      const changedKeys = Object.keys(form.formState.dirtyFields) as (keyof RoleFormValues)[];
      if (changedKeys.length === 0) {
        onClose();
        return;
      }
      const payload: UpdateRolePayload = Object.fromEntries(
        changedKeys.map((key) => [key, values[key]]),
      );
      updateMutation.mutate({ id: role.id, payload });
      return;
    }
    createMutation.mutate(buildCreatePayload(values));
  }

  function requestClose() {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }
    setIsDiscardDialogOpen(true);
  }

  return {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard: () => {
      setIsDiscardDialogOpen(false);
      onClose();
    },
    cancelDiscard: () => setIsDiscardDialogOpen(false),
  };
}
